import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dog, Calendar, CreditCard, MessageSquare, Award, ChevronRight, ShieldCheck } from 'lucide-react';
import WelcomeVideoBanner from '../../components/WelcomeVideoBanner';
import AnnouncementsBanner from '../../components/AnnouncementsBanner';

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    api.get('/dogs').then(r => setDogs(r.data)).catch(()=>{});
    api.get('/bookings').then(r => setBookings(r.data)).catch(()=>{});
  }, []);

  const upcoming = bookings.filter(b => new Date(b.start) > new Date()).slice(0, 5);
  const recent = bookings.slice(0, 5);

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
      <AnnouncementsBanner />
      <WelcomeVideoBanner />
      <div className='flex items-center justify-between flex-wrap gap-3'>
        <div>
          <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-[#C8102E]'>Welcome back</div>
          <h1 className='font-display text-3xl font-bold text-[#0B1220] mt-1' data-testid='client-dashboard-title'>{user?.full_name}</h1>
        </div>
        <Button className='bg-[#1B3A87] hover:bg-[#16306F] text-white' onClick={() => navigate('/client/book')} data-testid='client-book-now-button'>
          <Calendar className='w-4 h-4 mr-2' /> Book Boarding
        </Button>
      </div>

      <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6'>
        {[
          {l:'My Dogs',v:dogs.length, icon:Dog, color:'#1B3A87', testid:'kpi-dogs'},
          {l:'Upcoming',v:upcoming.length, icon:Calendar, color:'#2F6F5E', testid:'kpi-upcoming'},
          {l:'Total Bookings',v:bookings.length, icon:CreditCard, color:'#C8102E', testid:'kpi-bookings'},
          {l:'Member Status',v:user?.role === 'CLIENT' ? 'Standard' : user?.role, icon:Award, color:'#B08D57', testid:'kpi-member'},
        ].map((k) => (
          <Card key={k.l} className='p-5 kpi-card border border-slate-200 hover:shadow-md transition-shadow' data-testid={k.testid}>
            <div className='flex items-center justify-between'>
              <div className='text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500'>{k.l}</div>
              <k.icon className='w-4 h-4' style={{ color: k.color }} />
            </div>
            <div className='font-display text-3xl font-bold text-[#0B1220] tabular-nums'>{k.v}</div>
          </Card>
        ))}
      </div>

      <div className='grid lg:grid-cols-2 gap-5 mt-6'>
        <Card className='p-5 border border-slate-200'>
          <div className='flex items-center justify-between mb-3'>
            <h3 className='font-display font-semibold text-lg'>Upcoming bookings</h3>
            <Button variant='ghost' size='sm' onClick={() => navigate('/client/bookings')} data-testid='view-all-bookings'>View all <ChevronRight className='w-4 h-4 ml-1' /></Button>
          </div>
          {upcoming.length === 0 ? (
            <div className='text-sm text-slate-500 py-6 text-center'>
              No upcoming bookings. <button className='text-[#1B3A87] font-semibold hover:underline' onClick={() => navigate('/client/book')}>Book one now →</button>
            </div>
          ) : (
            <div className='space-y-2'>
              {upcoming.map((b) => (
                <div key={b.id} className='flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-[#1B3A87]/40'>
                  <div>
                    <div className='font-semibold text-sm'>{b.dog_name} · <span className='font-mono text-xs'>{b.run_name}</span></div>
                    <div className='text-xs text-slate-500'>{new Date(b.start).toLocaleDateString()} → {new Date(b.end).toLocaleDateString()} · {b.nights} nights</div>
                  </div>
                  <Badge className={b.status === 'PAID' ? 'bg-[#167A5A]/10 text-[#167A5A]' : 'bg-[#B45309]/10 text-[#B45309]'}>{b.status.replace('_',' ')}</Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className='p-5 border border-slate-200'>
          <div className='flex items-center justify-between mb-3'>
            <h3 className='font-display font-semibold text-lg'>My dogs</h3>
            <Button variant='ghost' size='sm' onClick={() => navigate('/client/dogs')} data-testid='view-all-dogs'>Manage <ChevronRight className='w-4 h-4 ml-1' /></Button>
          </div>
          {dogs.length === 0 ? (
            <div className='text-sm text-slate-500 py-6 text-center'>
              No dogs yet. <button className='text-[#1B3A87] font-semibold hover:underline' onClick={() => navigate('/client/dogs')}>Add your first dog →</button>
            </div>
          ) : (
            <div className='grid grid-cols-2 gap-3'>
              {dogs.slice(0,4).map((d) => (
                <div key={d.id} className='p-3 rounded-lg border border-slate-200 flex items-center gap-3'>
                  {d.photo_base64 ? (
                    <img src={d.photo_base64} alt={d.name} className='w-12 h-12 rounded-lg object-cover' />
                  ) : (
                    <div className='w-12 h-12 rounded-lg bg-[#1B3A87]/10 flex items-center justify-center font-display font-bold text-[#1B3A87]'>{d.name[0]}</div>
                  )}
                  <div className='min-w-0 flex-1'>
                    <div className='font-semibold text-sm truncate'>{d.name}</div>
                    <div className='text-xs text-slate-500 truncate'>{d.breed || '—'}</div>
                    {d.vax_compliant ? <Badge className='bg-[#167A5A]/10 text-[#167A5A] text-[10px] mt-1'><ShieldCheck className='w-3 h-3 mr-1' /> VAX OK</Badge> : <Badge className='bg-[#B45309]/10 text-[#B45309] text-[10px] mt-1'>VAX needed</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
